import axios from "axios";
import { EventEmitter } from "events";
import { createContext, useContext, useState } from "react";

const AdminContext = createContext();

const AdminEvents = new EventEmitter();

export const AdminProvider = ({ children }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  const [searchParams, setSearchParams] = useState({
    search: "",
  });

  // Update pagination state
  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  // Update search parameters
  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const createAdmin = async (data) => {
    try {
      const response = await axios.post(`/admins/`, data);
      const admin = response.data;

      setAdmins((prevData) => [admin, ...prevData]);

      // Émettre un événement pour notifier la mise à jour
      AdminEvents.emit("admin:created");

      return admin;
    } catch (error) {
      console.error("Error creating admin:", error);
      throw error;
    }
  };

  const getAdmins = async () => {
    try {
      setLoading(true);

      const params = {
        ...searchParams,
        page: pagination.page,
        limit: pagination.limit,
      };

      const response = await axios.get(`/admins`, { params });
      const { data, total, totalPages } = response.data;

      setAdmins(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      console.error("Error fetching admins:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getAdminById = async (adminId) => {
    try {
      const response = await axios.get(`/admins/${adminId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching admin by ID:", error);
      throw error;
    }
  };

  const updateAdmin = async (adminId, updatedData) => {
    try {
      const response = await axios.put(
        `/admins/${adminId}`,
        updatedData
      );
      const updatedAdmin = response.data;

      // Update the data in the context state
      setAdmins((prevData) =>
        prevData.map((data) =>
          data.id === updatedAdmin.id ? updatedAdmin : data
        )
      );

      // Émettre un événement pour notifier la mise à jour
      AdminEvents.emit("admin:updated");

      return updatedAdmin;
    } catch (error) {
      console.error("Error updating admin:", error);
      throw error;
    }
  };

  // Changer le rôle plateforme d'un admin (platform_admin ↔ platform_viewer).
  // Garde backend : pas de rétrogradation du dernier admin complet actif.
  const updateAdminRole = async (adminId, roleCode) => {
    try {
      const response = await axios.put(`/admins/${adminId}/role`, { roleCode });
      const updatedAdmin = response.data;
      setAdmins((prevData) =>
        prevData.map((data) =>
          data.id === updatedAdmin.id ? updatedAdmin : data
        )
      );
      AdminEvents.emit("admin:updated");
      return updatedAdmin;
    } catch (error) {
      console.error("Error updating admin role:", error);
      throw error;
    }
  };

  const resetAdminPassword = async (adminId) => {
    try {
      const response = await axios.put(
        `/admins/${adminId}/password/reset`
      );
      const updatedClient = response.data;
      return updatedClient;
    } catch (error) {
      console.error("Error resetting admin password:", error);
      throw error; // Propagate error to the caller
    }
  };

  const disconnectAdmin = async (adminId) => {
    try {
      await axios.post(`/admins/${adminId}/disconnect`);
    } catch (error) {
      console.error("Error disconnecting admin:", error);
      throw error;
    }
  };

  const deleteAdmin = async (adminId) => {
    try {
      await axios.delete(`/admins/${adminId}`);

      // Supprime l'élément supprimé de la liste
      setAdmins((prevData) => prevData.filter((data) => data.id !== adminId));

      // Mettre à jour la pagination (diminuer total)
      setPagination((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));
    } catch (error) {
      console.error("Error deleting admin:", error);
      throw error;
    }
  };

  // Context value
  const value = {
    admins,
    loading,
    pagination,
    searchParams,
    createAdmin,
    getAdmins,
    getAdminById,
    updateAdmin,
    updateAdminRole,
    deleteAdmin,
    disconnectAdmin,
    resetAdminPassword,
    updatePagination,
    updateSearchParams,
    AdminEvents,
  };

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

// Custom hook to use TeamContext
export const useAdminContext = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdminContext must be used within a AdminProvider");
  }
  return context;
};
